(function () {
  // CONFIG
  const DEFAULT_HEADER_OFFSET = 120; // adjust to your sticky header height
  const MAX_TOTAL_MS = 3500;         // total time to keep trying
  const RETRY_RAF_MS = 16;           // try every frame (~60fps)
  const TOLERANCE_PX = 3;            // how close to desired top is "good enough"

  console.log("[Vascular Dementia] fix-scroll.js loaded");

  // Helpers
  function getHeaderOffset() {
    const selectors = ['.site-header', '.header', 'header', '.sticky-header'];
    for (const s of selectors) {
      const el = document.querySelector(s);
      if (el) return Math.ceil(el.getBoundingClientRect().height);
    }
    return DEFAULT_HEADER_OFFSET;
  }

  function scrollToElWithOffset(el, offset, behavior = 'auto') {
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top, behavior });
  }

  function isElementAtExpectedPosition(el, offset) {
    if (!el) return false;
    const rectTop = el.getBoundingClientRect().top;
    // expected top relative to viewport is offset
    return Math.abs(rectTop - offset) <= TOLERANCE_PX;
  }

  // Prevent browser auto-restoration messing things up
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  async function waitForSelector(selector, timeout = 3000) {
    const start = Date.now();
    while (Date.now() - start < timeout) {
      const el = document.querySelector(selector);
      if (el) return el;
      await new Promise((r) => setTimeout(r, 80));
    }
    return null;
  }

  // The heavy-duty handler: keeps trying until good or timeout
  async function robustScrollToHash(hash, { offsetPx = null, behavior = 'auto' } = {}) {
    if (!hash) return;
    if (hash.charAt(0) !== '#') hash = '#' + hash;
    const selector = hash;
    const start = Date.now();
    const offset = offsetPx !== null ? offsetPx : getHeaderOffset();

    // Wait for the element to appear if necessary
    const el = await waitForSelector(selector, Math.min(1200, MAX_TOTAL_MS));
    if (!el) return;

    // Give the page one micro pause for fonts/images
    await new Promise((r) => setTimeout(r, 80));

    // If CSS supports scroll-margin-top on the element, set it as backup
    try {
      const computed = getComputedStyle(el);
      if (!computed.scrollMarginTop || computed.scrollMarginTop === '0px') {
        // add inline style only if not present
        el.style.scrollMarginTop = `${offset}px`;
      }
    } catch (e) {}

    // Use ResizeObserver to trigger a re-check if element or layout changes
    let ro;
    let roTriggered = false;
    try {
      ro = new ResizeObserver(() => {
        roTriggered = true;
      });
      ro.observe(document.documentElement);
      ro.observe(el);
    } catch (e) {
      ro = null;
    }

    // Repeatedly scroll until the element's top is ~offset or timeout
    return new Promise((resolve) => {
      function attempt() {
        // If reached timeout, stop
        if (Date.now() - start > MAX_TOTAL_MS) {
          if (ro) try { ro.disconnect(); } catch (e) {}
          return resolve(false);
        }

        // Do the scroll (try smooth only after first successful positioning)
        scrollToElWithOffset(el, offset, 'auto');

        // If it's close enough, one final smooth scroll for UX and finish
        if (isElementAtExpectedPosition(el, offset)) {
          // small delay then smooth adjust
          setTimeout(() => {
            scrollToElWithOffset(el, offset, behavior === 'smooth' ? 'smooth' : 'auto');
            try {
              const prevTab = el.getAttribute('tabindex');
              if (prevTab === null) el.setAttribute('tabindex', '-1');
              el.focus({ preventScroll: true });
              if (prevTab === null) el.removeAttribute('tabindex');
            } catch (e) {}
            if (ro) try { ro.disconnect(); } catch (e) {}
            return resolve(true);
          }, 30);
          return;
        }

        // otherwise try again next frame, or quickly if ResizeObserver triggered
        if (roTriggered) {
          roTriggered = false;
          // allow a tiny delay for layout settle then immediate attempt
          setTimeout(attempt, 20);
        } else {
          requestAnimationFrame(attempt);
        }
      }

      // initial attempt
      attempt();
    });
  }

  // Entrypoints: on load, DOMContentLoaded, hashchange and click intercepts
  function handleCurrentHashOnLoad() {
    if (window.location.hash) {
      // use auto offset detection and smooth UX
      robustScrollToHash(window.location.hash, { behavior: 'smooth' });
    }
  }

  window.addEventListener('load', function () {
    // Slightly longer wait on load to allow heavy assets to finish
    setTimeout(handleCurrentHashOnLoad, 160);
  });

  document.addEventListener('DOMContentLoaded', function () {
    // covers fast loads / SPA-like behavior
    setTimeout(handleCurrentHashOnLoad, 80);
  });

  window.addEventListener('hashchange', function () {
    robustScrollToHash(window.location.hash, { behavior: 'smooth' });
  });

  // Intercept same-page anchor clicks to control scroll immediately
  document.addEventListener('click', function (e) {
    const a = e.target.closest && e.target.closest('a[href^="#"]');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href || href === '#' || href.startsWith('#!')) return;

    e.preventDefault();
    const newHash = href;
    if (history.pushState) {
      history.pushState(null, '', newHash);
      robustScrollToHash(newHash, { behavior: 'smooth' });
    } else {
      window.location.hash = newHash;
    }
  });

})();
